// src/services/offline.js
import Dexie from 'dexie';
import { supabase } from './supabase'; 

export const db = new Dexie('MedicalAppDB');

// Updated schema - version 4 to include CHEW tables
db.version(5).stores({
  // Existing tables
  patients: '++localId, tempId, phone, synced, createdAt, agent_id, amount_paid',
  medicalRecords: '++localId, patientTempId, synced, createdAt, recorded_by',
  doctorNotes: '++localId, patient_id, synced, createdAt, doctor_id',
  pendingSync: '++id, type, action, createdAt, attempts',
  locations: 'id, name',
  clusters: 'id, name, market_location_id',
  
  // New tables for CHEW functionality
  emergencyContacts: '++localId, tempId, patient_id, synced, createdAt, created_by',
  patientEvaluations: '++localId, tempId, patient_id, chew_id, evaluation_type, status, synced, createdAt'
});

// Version 6: add checkin_notes table for offline check-in persistence
db.version(6).stores({
  checkin_notes: 'localId, patient_id, created_by, synced, supabase_checkin_id, created_at'
});

// Version 7: add popup_clinic_encounters table for offline popup clinic persistence
db.version(7).stores({
  popup_clinic_encounters: 'localId, patient_id, doctor_id, synced, supabase_encounter_id, created_at'
});

// Version 8: add who_health_assessments table for offline WHO assessment persistence
db.version(8).stores({
  who_health_assessments: 'localId, patient_id, recorded_by, synced, supabase_assessment_id, created_at'
});

// Version 9: add checkin_addendums table for offline addendum persistence
db.version(9).stores({
  checkin_addendums: 'localId, checkin_note_id, added_by, synced, supabase_addendum_id, created_at'
});

// Handle version migration
db.on('ready', function() {
  return db.on('versionchange', function(event) {
    if (event.newVersion === null) {
      // Database is being deleted
      console.log('Database is being deleted');
    }
  });
});

// Initialize offline database
export const initOfflineDB = async () => {
  try {
    // Test database access
    await db.open();
    
    // Clear old data
    await db.locations.clear();
    await db.clusters.clear();
    
    // Load locations and clusters from Supabase
    const { data: locations, error: locError } = await supabase
      .from('market_locations')
      .select('*');
      
    const { data: clusters, error: clusterError } = await supabase
      .from('market_clusters')
      .select('*');
    
    if (locError) {
      console.error('Error fetching locations:', locError);
    }
    
    if (clusterError) {
      console.error('Error fetching clusters:', clusterError);
    }
      
    // Store in offline DB
    if (locations && locations.length > 0) {
      await db.locations.bulkPut(locations);
    }
    
    if (clusters && clusters.length > 0) {
      await db.clusters.bulkPut(clusters);
    }
  } catch (error) {
    console.error('Error initializing offline DB:', error);
    // Try to delete and recreate the database if it's corrupted
    if (error.name === 'VersionError' || error.name === 'InvalidStateError') {
      await db.delete();
      window.location.reload();
    }
    throw error;
  }
};

// Helper function to save emergency contact offline
export const saveEmergencyContactOffline = async (contactData) => {
  const tempId = 'offline_ec_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  
  const offlineContact = {
    ...contactData,
    tempId: tempId,
    synced: false,
    createdAt: new Date().toISOString()
  };
  
  await db.emergencyContacts.add(offlineContact);
  
  // Add to sync queue
  await db.pendingSync.add({
    type: 'emergency_contact',
    action: 'create',
    data: contactData,
    tempId: tempId,
    attempts: 0,
    createdAt: new Date().toISOString()
  });
  
  return tempId;
};

// Helper function to save evaluation offline
export const saveEvaluationOffline = async (evaluationData) => {
  const tempId = 'offline_eval_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  
  const offlineEvaluation = {
    ...evaluationData,
    tempId: tempId,
    synced: false,
    createdAt: new Date().toISOString()
  };
  
  await db.patientEvaluations.add(offlineEvaluation);
  
  // Add to sync queue
  await db.pendingSync.add({
    type: 'patient_evaluation',
    action: 'create',
    data: evaluationData,
    tempId: tempId,
    attempts: 0,
    createdAt: new Date().toISOString()
  });
  
  return tempId;
};

// Helper function to get offline evaluations for a patient
export const getOfflineEvaluations = async (patientId) => {
  try {
    const evaluations = await db.patientEvaluations
      .where('patient_id')
      .equals(patientId)
      .toArray();
    
    return evaluations;
  } catch (error) {
    console.error('Error fetching offline evaluations:', error);
    return [];
  }
};

// Helper function to get offline emergency contacts for a patient
export const getOfflineEmergencyContacts = async (patientId) => {
  try {
    const contacts = await db.emergencyContacts
      .where('patient_id')
      .equals(patientId)
      .toArray();
    
    return contacts;
  } catch (error) {
    console.error('Error fetching offline emergency contacts:', error);
    return [];
  }
};

// Helper function to update evaluation status offline
export const updateEvaluationStatusOffline = async (tempId, status) => {
  try {
    const evaluation = await db.patientEvaluations
      .where('tempId')
      .equals(tempId)
      .first();
    
    if (evaluation) {
      await db.patientEvaluations.update(evaluation.localId, { 
        status: status,
        updated_at: new Date().toISOString()
      });
      
      // Update in sync queue
      const syncItem = await db.pendingSync
        .where('tempId')
        .equals(tempId)
        .first();
      
      if (syncItem) {
        await db.pendingSync.update(syncItem.id, {
          data: { ...syncItem.data, status: status }
        });
      }
    }
  } catch (error) {
    console.error('Error updating evaluation status offline:', error);
  }
};

// Helper function to clear all offline data (useful for debugging)
export const clearOfflineData = async () => {
  await db.patients.clear();
  await db.medicalRecords.clear();
  await db.doctorNotes.clear();
  await db.pendingSync.clear();
  await db.emergencyContacts.clear();
  await db.patientEvaluations.clear();
  // Don't clear locations and clusters as they're reference data
};

// Save a checkin addendum offline — call this when navigator.onLine is false.
// Only called when checkinNote.id is a real Supabase UUID (unsynced check-ins are blocked earlier).
export const saveCheckinAddendumOffline = async (addendumData) => {
  const localId = 'offline_addendum_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

  await db.checkin_addendums.add({
    localId,
    checkin_note_id: addendumData.checkin_note_id,
    added_by: addendumData.added_by,
    payload: addendumData,
    synced: false,
    supabase_addendum_id: null,  // checkpoint: set after insert succeeds
    sync_error: null,
    created_at: new Date().toISOString()
  });

  return localId;
};

// Save a WHO health assessment offline — call this when navigator.onLine is false.
// Receives the post-coercion assessmentData object so syncWHOAssessments can replay
// the exact same object without re-running numeric conversions.
export const saveWHOAssessmentOffline = async (assessmentData) => {
  const localId = 'offline_who_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

  await db.who_health_assessments.add({
    localId,
    patient_id: assessmentData.patient_id,
    recorded_by: assessmentData.recorded_by,
    assessment_date: assessmentData.assessment_date,
    payload: assessmentData,
    synced: false,
    supabase_assessment_id: null,  // checkpoint: set after insert succeeds
    sync_error: null,
    created_at: new Date().toISOString()
  });

  return localId;
};

// Save a popup clinic encounter offline — call this when navigator.onLine is false.
// Only applies to new encounters; edits require the existing Supabase row ID.
export const savePopupEncounterOffline = async (payload) => {
  const localId = 'offline_popup_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

  await db.popup_clinic_encounters.add({
    localId,
    patient_id: payload.patient_id,
    doctor_id: payload.doctor_id,
    encounter_date: payload.encounter_date,
    payload,
    synced: false,
    supabase_encounter_id: null,  // checkpoint: set after insert succeeds
    sync_error: null,
    created_at: new Date().toISOString()
  });

  return localId;
};

// Save a check-in offline — call this when navigator.onLine is false.
// Stores the full payload needed to replay the online write sequence at sync time.
export const saveCheckinOffline = async (payload) => {
  const localId = 'offline_checkin_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

  await db.checkin_notes.add({
    localId,
    patient_id: payload.checkinRecord.patient_id,
    created_by: payload.checkinRecord.created_by,
    created_by_role: payload.checkinRecord.created_by_role,
    checkin_date: payload.checkinRecord.checkin_date,
    checkin_time: new Date().toISOString(),
    payload,
    synced: false,
    sync_error: null,
    supabase_checkin_id: null,  // checkpoint: set after step 1 succeeds
    line_items_synced: false,   // checkpoint: set after step 2 succeeds
    created_at: new Date().toISOString()
  });

  return localId;
};

// Replay all unsynced check-ins against Supabase, mirroring the online write
// sequence in CheckinNote.js: checkin_notes → line_items → payments → wallet RPCs → plan update.
// Checkpoints (supabase_checkin_id, line_items_synced) prevent duplicate inserts on retry.
export const syncCheckins = async () => {
  const allRecords = await db.checkin_notes.toArray();
  const unsynced = allRecords.filter(r => !r.synced);

  for (const record of unsynced) {
    try {
      const { payload } = record;
      let checkinId = record.supabase_checkin_id || null;

      // Step 1: insert checkin_notes row.
      // Guard: if supabase_checkin_id is already set from a prior failed attempt,
      // skip the insert entirely — reuse the existing Supabase record.
      if (!checkinId) {
        const { data: checkin, error: checkinError } = await supabase
          .from('checkin_notes')
          .insert([payload.checkinRecord])
          .select()
          .single();

        if (checkinError) throw checkinError;

        checkinId = checkin.id;

        // Persist checkpoint immediately so that if step 2+ fails, the next retry
        // finds this ID and skips the insert rather than creating a duplicate.
        await db.checkin_notes.update(record.localId, { supabase_checkin_id: checkinId });
      }

      // Step 2: insert line items and payment records.
      // Guard: if line_items_synced is already true, skip — items were written in a prior attempt.
      if (payload.usesLineItems && payload.lineItemsTemplate.length > 0 && !record.line_items_synced) {
        const lineItems = payload.lineItemsTemplate.map(item => ({
          ...item,
          checkin_id: checkinId
        }));

        const { data: insertedItems, error: lineItemsError } = await supabase
          .from('checkin_line_items')
          .insert(lineItems)
          .select();

        if (lineItemsError) throw lineItemsError;

        const payments = insertedItems
          .filter(item => Number(item.amount_paid) > 0)
          .map(item => ({
            line_item_id: item.id,
            payment_amount: Number(item.amount_paid),
            payment_date: record.created_at,
            collected_by: payload.performedBy,
            payment_method: payload.paymentMethod,
            notes: 'Payment at check-in (synced from offline)'
          }));

        if (payments.length > 0) {
          const { error: paymentsError } = await supabase
            .from('line_item_payments')
            .insert(payments);

          if (paymentsError) throw paymentsError;
        }

        // Persist checkpoint so a failure in step 3 does not re-insert line items.
        await db.checkin_notes.update(record.localId, { line_items_synced: true });
      }

      // Step 3: wallet transactions.
      // NOTE: process_wallet_transaction is not idempotent. If the app crashes between
      // the credit and debit calls, the completed call will be replayed on the next retry,
      // resulting in a duplicate wallet entry. Full idempotency here requires server-side
      // deduplication keyed on the offline localId — accepted limitation for now.
      if (payload.cashTopup > 0) {
        const { error: cashError } = await supabase.rpc('process_wallet_transaction', {
          p_patient_id: payload.patientId,
          p_transaction_type: 'credit',
          p_amount: payload.cashTopup,
          p_reference_type: 'cash_payment',
          p_description: 'Cash payment at check-in',
          p_performed_by: payload.performedBy,
          p_reference_id: checkinId
        });

        if (cashError) throw cashError;
      }

      if (payload.totalPaying > 0) {
        const { error: debitError } = await supabase.rpc('process_wallet_transaction', {
          p_patient_id: payload.patientId,
          p_transaction_type: 'debit',
          p_amount: payload.totalPaying,
          p_reference_type: 'checkin_services',
          p_description: `Services at check-in: ${payload.serviceDescriptionStr}`,
          p_performed_by: payload.performedBy,
          p_reference_id: checkinId
        });

        if (debitError) throw debitError;
      }

      // Step 4: update patient plan if one was purchased
      if (payload.updatePlan && payload.planType) {
        await supabase
          .from('patients')
          .update({
            current_plan: payload.planType,
            last_plan_date: record.created_at
          })
          .eq('id', payload.patientId);
      }

      // All steps succeeded — mark as synced
      await db.checkin_notes.update(record.localId, {
        synced: true,
        sync_error: null
      });

    } catch (error) {
      console.error('Error syncing offline check-in:', error);
      // Leave synced: false so this record retries on the next sync cycle.
      // supabase_checkin_id and line_items_synced are preserved as checkpoints.
      await db.checkin_notes.update(record.localId, {
        sync_error: error.message
      });
    }
  }
};

// Replay all unsynced popup clinic encounters against Supabase.
// Single-step sync: insert → checkpoint → mark synced.
// The supabase_encounter_id checkpoint prevents duplicate inserts on retry.
export const syncPopupEncounters = async () => {
  const allRecords = await db.popup_clinic_encounters.toArray();
  const unsynced = allRecords.filter(r => !r.synced);

  for (const record of unsynced) {
    try {
      let encounterId = record.supabase_encounter_id || null;

      // Guard: if supabase_encounter_id is already set from a prior failed attempt,
      // skip the insert — the row already exists in Supabase.
      if (!encounterId) {
        const { data: encounter, error } = await supabase
          .from('popup_clinic_encounters')
          .insert([record.payload])
          .select()
          .single();

        if (error) throw error;

        encounterId = encounter.id;

        // Persist checkpoint immediately so a failure before synced: true
        // does not cause a duplicate insert on the next retry.
        await db.popup_clinic_encounters.update(record.localId, {
          supabase_encounter_id: encounterId
        });
      }

      await db.popup_clinic_encounters.update(record.localId, {
        synced: true,
        sync_error: null
      });
    } catch (error) {
      console.error('Error syncing offline popup clinic encounter:', error);
      await db.popup_clinic_encounters.update(record.localId, {
        sync_error: error.message
      });
    }
  }
};

// Replay all unsynced WHO health assessments against Supabase.
// Single-step sync: insert → checkpoint → mark synced.
// The supabase_assessment_id checkpoint prevents duplicate inserts on retry.
export const syncWHOAssessments = async () => {
  const allRecords = await db.who_health_assessments.toArray();
  const unsynced = allRecords.filter(r => !r.synced);

  for (const record of unsynced) {
    try {
      let assessmentId = record.supabase_assessment_id || null;

      // Guard: if supabase_assessment_id is already set from a prior failed attempt,
      // skip the insert — the row already exists in Supabase.
      if (!assessmentId) {
        const { data: assessment, error } = await supabase
          .from('who_health_assessments')
          .insert([record.payload])
          .select()
          .single();

        if (error) throw error;

        assessmentId = assessment.id;

        // Persist checkpoint immediately so a failure before synced: true
        // does not cause a duplicate insert on the next retry.
        await db.who_health_assessments.update(record.localId, {
          supabase_assessment_id: assessmentId
        });
      }

      await db.who_health_assessments.update(record.localId, {
        synced: true,
        sync_error: null
      });
    } catch (error) {
      console.error('Error syncing offline WHO health assessment:', error);
      await db.who_health_assessments.update(record.localId, {
        sync_error: error.message
      });
    }
  }
};

// Replay all unsynced checkin addendums against Supabase.
// Uses a plain insert without the profile join — the join is only needed for the
// local state update in CheckinAddendum.js, not for storage.
export const syncCheckinAddendums = async () => {
  const allRecords = await db.checkin_addendums.toArray();
  const unsynced = allRecords.filter(r => !r.synced);

  for (const record of unsynced) {
    try {
      let addendumId = record.supabase_addendum_id || null;

      // Guard: if supabase_addendum_id is already set from a prior failed attempt,
      // skip the insert — the row already exists in Supabase.
      if (!addendumId) {
        const { data: addendum, error } = await supabase
          .from('checkin_addendums')
          .insert([record.payload])
          .select()
          .single();

        if (error) throw error;

        addendumId = addendum.id;

        // Persist checkpoint immediately so a failure before synced: true
        // does not cause a duplicate insert on the next retry.
        await db.checkin_addendums.update(record.localId, {
          supabase_addendum_id: addendumId
        });
      }

      await db.checkin_addendums.update(record.localId, {
        synced: true,
        sync_error: null
      });
    } catch (error) {
      console.error('Error syncing offline checkin addendum:', error);
      await db.checkin_addendums.update(record.localId, {
        sync_error: error.message
      });
    }
  }
};

// Export database instance
export default db;